import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DemoDialog = ({ open, onOpenChange, language = "en", copy }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusCopy, setStatusCopy] = useState("");

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setSubmitting(false);
      setStatusCopy("");
    }
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.get("name"),
          organization: formData.get("organization"),
          email: formData.get("email"),
          interest: formData.get("interest"),
          language,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || "Unable to send demo request.");
      }
      const userMessage = result.configuration_required ? copy.form.successCopy : (result.message || copy.form.successCopy);
      setStatusCopy(userMessage);
      setSubmitted(true);
      toast.success(result.email_sent ? "Demo request emailed" : "Demo request received", { description: userMessage });
    } catch (error) {
      toast.error("Demo request could not be sent", { description: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="demo-dialog" className="demo-dialog">
        <DialogHeader data-testid="demo-dialog-header">
          <DialogTitle data-testid="demo-dialog-title" className="dialog-title">{copy.form.title}</DialogTitle>
          <DialogDescription data-testid="demo-dialog-description" className="dialog-description">
            {copy.form.description}
          </DialogDescription>
        </DialogHeader>
        {submitted ? (
          <div data-testid="demo-form-success-message" className="success-panel">
            <span data-testid="demo-form-success-kicker">Request captured</span>
            <h3 data-testid="demo-form-success-title">{copy.form.successTitle}</h3>
            <p data-testid="demo-form-success-copy">{statusCopy || copy.form.successCopy}</p>
            <Button data-testid="demo-form-close-button" onClick={() => onOpenChange(false)} className="primary-action">{copy.form.close}</Button>
          </div>
        ) : (
          <form data-testid="demo-request-form" onSubmit={handleSubmit} className="demo-form">
            <div data-testid="demo-form-name-field" className="form-field">
              <Label data-testid="demo-form-name-label" htmlFor="name">{copy.form.name}</Label>
              <Input data-testid="demo-form-name-input" id="name" name="name" required placeholder={copy.form.placeholders.name} />
            </div>
            <div data-testid="demo-form-organization-field" className="form-field">
              <Label data-testid="demo-form-organization-label" htmlFor="organization">{copy.form.organization}</Label>
              <Input data-testid="demo-form-organization-input" id="organization" name="organization" required placeholder={copy.form.placeholders.organization} />
            </div>
            <div data-testid="demo-form-email-field" className="form-field">
              <Label data-testid="demo-form-email-label" htmlFor="email">{copy.form.email}</Label>
              <Input data-testid="demo-form-email-input" id="email" name="email" type="email" required placeholder={copy.form.placeholders.email} />
            </div>
            <div data-testid="demo-form-interest-field" className="form-field full-field">
              <Label data-testid="demo-form-interest-label" htmlFor="interest">{copy.form.interest}</Label>
              <Textarea data-testid="demo-form-interest-input" id="interest" name="interest" placeholder={copy.form.placeholders.interest} />
            </div>
            <Button data-testid="demo-form-submit-button" type="submit" disabled={submitting} className="primary-action full-field">{submitting ? copy.form.sending : copy.form.submit}</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};